/* eslint-disable prefer-rest-params, prefer-spread, @typescript-eslint/no-non-null-assertion */
import { getOwnPropertyDescriptors } from './helpers/object_helpers';
function addPluginAndDependenciesToMap(map, plugin) {
    if (!plugin)
        return;
    if (typeof plugin == 'string') {
        // Add a placeholder indicating a named dependency to the map
        if (Object(map[plugin]) !== map[plugin])
            map[plugin] = true;
        return;
    }
    if (Object(plugin) !== plugin || !plugin.pluginName) {
        throw new Error('Plugin must have a name');
    }
    (plugin.dependencies || []).forEach((dep) => {
        addPluginAndDependenciesToMap(map, dep);
    });
    map[plugin.pluginName] = plugin;
}
function createPluginListFromMap(map) {
    return Object.keys(map).map((key) => {
        const plugin = map[key];
        return Object(plugin) === plugin ? plugin : key;
    });
}
function initPluginNode(node, args, plugin) {
    const pluginInit = typeof plugin.nodeInit == 'function' ? plugin.nodeInit : null;
    if (!pluginInit)
        return;
    pluginInit.apply(node, args.splice(0, pluginInit.length || 0));
}
function initPluginRoot(root, plugin) {
    const pluginInit = typeof plugin.rootInit == 'function' ? plugin.rootInit : null;
    if (!pluginInit)
        return;
    pluginInit.call(root);
}
function setSuperOnOverrideFunction(override, sup, name) {
    if (typeof sup != 'function')
        return;
    if (typeof override != 'function')
        return;
    if ('_super' in override) {
        throw new Error('Override function must be unique: ' + name);
    }
    Object.defineProperty(override, '_super', {
        value: sup,
        configurable: false,
        enumerable: false,
        writable: false,
    });
}
function installOnObject(source, destination, allowOverride) {
    const sourceDescriptors = getOwnPropertyDescriptors(source);
    const destinationDescriptors = getOwnPropertyDescriptors(destination);
    Object.keys(sourceDescriptors).forEach(function forEachSourceDescriptor(prop) {
        const newDescriptor = sourceDescriptors[prop];
        const oldDescriptor = destinationDescriptors[prop];
        // Ignore the constructor property
        if (prop === 'constructor')
            return;
        if (oldDescriptor && newDescriptor) {
            if (!oldDescriptor.configurable) {
                throw new Error('Cannot override non-configurable property: ' + prop);
            }
            if (!allowOverride) {
                throw new Error('Override not allowed: ' + prop);
            }
            if ('value' in newDescriptor) {
                setSuperOnOverrideFunction(newDescriptor.value, oldDescriptor.value, prop);
                newDescriptor.writable = newDescriptor.writable && oldDescriptor.writable;
            }
            else if ('set' in newDescriptor || 'get' in newDescriptor) {
                setSuperOnOverrideFunction(newDescriptor.get, oldDescriptor.get, 'get ' + prop);
                setSuperOnOverrideFunction(newDescriptor.set, oldDescriptor.set, 'set ' + prop);
            }
            newDescriptor.enumerable = newDescriptor.enumerable && oldDescriptor.enumerable;
        }
        Object.defineProperty(destination, prop, newDescriptor);
    });
}
export default function createRoot(...newPlugins) {
    const plugins = [];
    const pluginsMap = {};
    let root = null;
    let nodesExist = false;
    let addPluginGuard = false;
    function Node(args) {
        plugins.forEach(initPluginNode.bind(null, this, args || []));
        // If we have a known root, then we're building a regular node
        if (root && this !== root)
            nodesExist = true;
        // include "checkAncestry()" method ?
    }
    Object.defineProperties(Node.prototype, {
        constructor: {
            value: Node,
            configurable: false,
            enumerable: false,
            writable: false,
        },
    });
    Node.prototype.create = function create() {
        const argsLen = arguments.length;
        const args = new Array(argsLen);
        for (let i = 0; i < argsLen; ++i) {
            // eslint-disable-next-line prefer-rest-params
            args[i] = arguments[i];
        }
        return new Node(args);
    };
    function setRootOnNodePrototype(seal) {
        Object.defineProperties(Node.prototype, {
            root: {
                value: root,
                configurable: !seal,
                enumerable: false,
                writable: false,
            },
        });
    }
    // Placeholder on the prototype to avoid mutating shape of prototype after instances are created
    setRootOnNodePrototype(false);
    function RootNode() {
        if (this.constructor !== RootNode)
            return;
        // Never build another root object
        if (root) {
            if (this !== root)
                return root;
        }
        else {
            root = this;
            setRootOnNodePrototype(true);
        }
        // A non extensible object whose prototype is the pluginsMap allows
        // Accessing the map while preventing unwanted modifications
        // TODO: Use Proxy for prototype object ?
        this.plugins = Object.preventExtensions(Object.create(pluginsMap, {
            add: {
                value: guardedAddPlugin,
            },
        }));
        Node.call(this, []);
        plugins.forEach(initPluginRoot.bind(null, this));
    }
    RootNode.prototype = Object.create(Node.prototype, {
        constructor: {
            value: RootNode,
            configurable: false,
            enumerable: false,
            writable: false,
        },
    });
    RootNode.prototype.createRoot = createRoot;
    function installPlugin(plugin) {
        installOnObject(plugin.nodeMethods || {}, Node.prototype);
        if (typeof plugin.getNodeMethodOverrides == 'function') {
            installOnObject(plugin.getNodeMethodOverrides(), Node.prototype, true);
        }
        installOnObject(plugin.rootMethods || {}, RootNode.prototype);
        if (typeof plugin.getRootMethodOverrides == 'function') {
            installOnObject(plugin.getRootMethodOverrides(), RootNode.prototype, true);
        }
        plugins.push(plugin);
        pluginsMap[plugin.pluginName] = plugin;
    }
    function addPlugins( /* plugins */) {
        const newPluginsMap = {};
        let newPlugins = null;
        // Flatten any plugins or array of plugins passed as arguments
        Array.prototype.concat
            .apply([], arguments)
            // And populate the map of resulting plugins instances
            .forEach(addPluginAndDependenciesToMap.bind(null, newPluginsMap));
        const overlappingPluginNames = Object.keys(newPluginsMap).filter(function existingPluginButDifferent(name) {
            return pluginsMap[name] && pluginsMap[name] !== newPluginsMap[name];
        });
        if (overlappingPluginNames.length > 0) {
            throw new Error('Cannot replace already installed plugins: ' + overlappingPluginNames.join(', '));
        }
        newPlugins = createPluginListFromMap(newPluginsMap);
        const missingPluginNames = newPlugins.filter(function nonObject(newPlugin) {
            return Object(newPlugin) !== newPlugin;
        });
        if (missingPluginNames.length > 0) {
            throw new Error('Cannot add plugins, missing dependencies: ' + missingPluginNames.join(', '));
        }
        newPlugins = newPlugins.filter(function existingPlugin(newPlugin) {
            return !pluginsMap[newPlugin.pluginName];
        });
        if (nodesExist &&
            newPlugins.some(function pluginHasNodeInit(newPlugin) {
                return !!newPlugin.nodeInit;
            })) {
            throw new Error('Cannot add plugin after nodes already exist.');
        }
        newPlugins.forEach(installPlugin);
        // We haven't created the root yet, nothing to initialize
        if (!root)
            return;
        newPlugins.forEach(initPluginNode.bind(null, root, []));
        newPlugins.forEach(initPluginRoot.bind(null, root));
    }
    function guardedAddPlugin() {
        if (addPluginGuard) {
            throw new Error('Already adding plugins');
        }
        try {
            addPluginGuard = true;
            addPlugins.apply(null, arguments);
        }
        finally {
            addPluginGuard = false;
        }
    }
    addPlugins.apply(null, Array.prototype.concat.apply(
    // Take existing plugins of this root, if any, as a base
    createPluginListFromMap((this && typeof this.plugins == 'object' && Object.getPrototypeOf(this.plugins)) || {}), 
    // And add plugins or array of plugins passed as arguments
    arguments));
    return new RootNode();
}
//# sourceMappingURL=arborist.js.map